import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LoginDto } from '../models/lib/login-request.model';
import { RegisterDto } from '../models/lib/register-request.model';
import { LoginResponseDto } from '../models/lib/login-response.model';
import { CheckActiveSessionDto } from '../models/lib/check-active-session.model';
import { UserSettingsDto } from '../models/lib/user-settings.model';
import { OrganizationDto } from '../models/lib/organization.model';
import { ResponseModel } from '../models/lib/response.model';
import { UserDto } from '../models/lib/user.model';
import { RefreshTokenModelDto } from '../models/lib/refresh-token-model.model';
import { SubscriptionGetAllByFilterRequest, SubscriptionResponseModel } from '../models/lib/subscription.model';
import { buildApiUrl, buildRequestHeaders } from '@dental-ui/shared/consts';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private readonly http = inject(HttpClient);


  /**
   * Kullanıcı kimlik bilgilerini backend'e gönderir ve login response'unu döner.
   * `authorize` flag'i, bazı endpoint'lerin isteğe bağlı olarak Authorization header'ı eklemesini sağlar.
   */
  login(
    loginRequest: LoginDto,
    authorize = false
  ): Observable<ResponseModel<LoginResponseDto>> {
    return this.http.post<ResponseModel<LoginResponseDto>>(
      this.buildApiUrl('auth.login'),
      loginRequest,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  /**
   * Login öncesi aktif oturum olup olmadığını kontrol eder.
   * hasActiveSession true ise kullanıcıya diğer oturumların kapanacağı bildirilir.
   */
  checkActiveSession(
    loginRequest: LoginDto,
    authorize = false
  ): Observable<ResponseModel<CheckActiveSessionDto>> {
    return this.http.post<ResponseModel<CheckActiveSessionDto>>(
      this.buildApiUrl('auth.checkActiveSession'),
      {
        email: loginRequest.email,
        password: loginRequest.password,
      },
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  /**
   * Kullanıcı kayıt işlemini gerçekleştirir
   * @param registerRequest - Kayıt bilgileri
   * @param token - Kayıt token'ı (query parametresi olarak gönderilir)
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: false)
   * @returns ResponseModel
   */
  register(
    registerRequest: RegisterDto,
    token?: string | null,
    authorize = false
  ): Observable<ResponseModel<any>> {
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    return this.http.post<ResponseModel<any>>(
      `${this.buildApiUrl('auth.register')}${tokenQuery}`,
      registerRequest,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(
      this.buildApiUrl('auth.logout'),
      {},
      {
        headers: buildRequestHeaders(true),
      }
    );
  }

  refreshToken(
    email: string,
    accessToken: string,
    refreshToken: string
  ): Observable<ResponseModel<LoginResponseDto>> {
    return this.http.post<ResponseModel<LoginResponseDto>>(
      this.buildApiUrl('auth.refreshToken'),
      {
        email: email,
        accessToken: accessToken,
        refreshToken: refreshToken,
      },
      {
        headers: buildRequestHeaders(false), // Don't send auth header for refresh
      }
    );
  }

  me(refreshTokenModel: RefreshTokenModelDto): Observable<ResponseModel<LoginResponseDto>> {
    return this.http.post<ResponseModel<LoginResponseDto>>(
      this.buildApiUrl('auth.me'),
      refreshTokenModel,
      {
        headers: buildRequestHeaders(true), // Don't send auth header for refresh
      }
    );
  }

  /**
   * Parola sıfırlama isteği gönderir
   * @param email - Parola sıfırlama isteği gönderilecek e-posta adresi
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: false)
   * @returns ResponseModel
   */
  forgetPassword(
    email: string,
    authorize = false
  ): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(
      this.buildApiUrl('auth.forgetPassword'),
      { email },
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  /**
   * Parola değiştirme isteği gönderir
   * @param token - Parola değiştirme token'ı
   * @param newPassword - Yeni parola
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: false)
   * @returns ResponseModel
   */
  changePassword(
    token: string,
    newPassword: string,
    authorize = false
  ): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(
      `${this.buildApiUrl('auth.changePassword')}?value=${encodeURIComponent(token)}`,
      { newPassword },
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  /**
   * Kullanıcı aktivasyon işlemini gerçekleştirir
   * @param token - Aktivasyon token'ı (query parametresi olarak gönderilir)
   * @param authorize - Authorization header'ı eklenip eklenmeyeceği (varsayılan: false)
   * @returns ResponseModel
   */
  userActivation(
    token: string,
    authorize = false
  ): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(
      `${this.buildApiUrl('auth.userActivation')}?token=${encodeURIComponent(token)}`,
      {},
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  /**
   * Sends a new activation email to an unactivated account.
   */
  resendActivationEmail(
    email: string,
    authorize = false
  ): Observable<ResponseModel<unknown>> {
    return this.http.post<ResponseModel<unknown>>(
      this.buildApiUrl('auth.resendActivationEmail'),
      JSON.stringify(email),
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  getUser(
    userId: string,
    authorize = true
  ): Observable<ResponseModel<UserDto>> {
    return this.http.get<ResponseModel<UserDto>>(
      `${this.buildApiUrl('user.getById')}?user=${encodeURIComponent(userId)}`,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  updateUser(
    userId: string,
    payload: UserDto,
    authorize = true
  ): Observable<ResponseModel<UserDto>> {
    const body: Partial<UserDto> = {
      ...payload,
      id: userId ?? payload.id,
    };

    return this.http.put<ResponseModel<UserDto>>(
      this.buildApiUrl('user.update'),
      body,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  getUserSettings(
    userId: string,
    authorize = true
  ): Observable<ResponseModel<UserSettingsDto | null>> {
    return this.http.get<ResponseModel<UserSettingsDto | null>>(
      `${this.buildApiUrl('userSettings.getByUser')}?user=${encodeURIComponent(userId)}`,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  createUserSettings(
    payload: UserSettingsDto,
    authorize = true
  ): Observable<ResponseModel<UserSettingsDto>> {
    return this.http.post<ResponseModel<UserSettingsDto>>(
      this.buildApiUrl('userSettings.create'),
      payload,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  updateUserSettings(
    payload: UserSettingsDto,
    authorize = true
  ): Observable<ResponseModel<UserSettingsDto>> {
    return this.http.put<ResponseModel<UserSettingsDto>>(
      this.buildApiUrl('userSettings.update'),
      payload,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  getUserOrganizations(
    userId: string,
    authorize = true
  ): Observable<ResponseModel<OrganizationDto[]>> {
    return this.http.get<ResponseModel<OrganizationDto[]>>(
      `${this.buildApiUrl('userOrganization.getAllByUser')}?user=${encodeURIComponent(userId)}`,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  createOrganization(
    payload: { userId?: string; description: string; isActive: boolean },
    authorize = true
  ): Observable<ResponseModel<OrganizationDto>> {
    return this.http.post<ResponseModel<OrganizationDto>>(
      this.buildApiUrl('organization.create'),
      payload,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  createUserOrganization(
    payload: { userId?: string; organizationId?: string },
    authorize = true
  ): Observable<ResponseModel<any>> {
    return this.http.post<ResponseModel<any>>(
      this.buildApiUrl('userOrganization.create'),
      payload,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  deleteUserOrganization(
    userOrganizationId: string,
    authorize = true
  ): Observable<ResponseModel<any>> {
    return this.http.delete<ResponseModel<any>>(
      `${this.buildApiUrl('userOrganization.delete')}?userOrganization=${encodeURIComponent(userOrganizationId)}`,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  changeUserOrganization(
    payload: { id?: string; organizationId?: string },
    authorize = true
  ): Observable<ResponseModel<UserDto>> {
    return this.http.put<ResponseModel<UserDto>>(
      this.buildApiUrl('user.changeOrganization'),
      payload,
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  getActiveSubscriptions(
    request: SubscriptionGetAllByFilterRequest,
    authorize = true
  ): Observable<ResponseModel<SubscriptionResponseModel>> {
    return this.http.post<ResponseModel<SubscriptionResponseModel>>(
      this.buildApiUrl('subscription.getAllByFilter'),
      {
        PaginationRequestModel: request.PaginationRequestModel,
        SubscriptionFilterRequestDto: request.SubscriptionFilterRequestDto,
      },
      {
        headers: buildRequestHeaders(authorize),
      }
    );
  }

  /**
   * Endpoint path'inden tam API URL'i oluşturur
   * @param endpointPath - Nokta ile ayrılmış endpoint path'i (örn: 'auth.login')
   * @returns Tam URL string'i
   */
  protected buildApiUrl(endpointPath: string): string {
    return buildApiUrl(endpointPath);
  }

}

